/**
 * OnTimeRuleEditor — how strict "on time" is for this company (DEC-326 item 5, Corbin
 * 2026-10-13). Reads / saves GET / PATCH /v1/tenants/on-time-settings (MANAGE_TENANT).
 *
 * Loads are compared with earlier loads on the same route (same job type, pickup and drop)
 * over the last 90 days, from all drivers, once there are at least 5 of them. The rule
 * decides how much slower than that history still counts as on time.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';

import { describeRule, parseRule, type OnTimeMethod, type OnTimeRule } from './on-time-rule';

const help: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

export function OnTimeRuleEditor(): React.JSX.Element {
  const opts = useApiOpts();
  const [saved, setSaved] = React.useState<OnTimeRule | null>(null);
  const [method, setMethod] = React.useState<OnTimeMethod>('ALLOWANCE');
  const [text, setText] = React.useState('');
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const apply = (rule: OnTimeRule): void => {
    setSaved(rule);
    setMethod(rule.method);
    setText(String(rule.value));
  };

  const load = React.useCallback(async (): Promise<void> => {
    try {
      const res = await authedFetch(opts, '/v1/tenants/on-time-settings');
      if (!res.ok) {
        setLoadError(`Could not load the on-time rule (${String(res.status)}).`);
        return;
      }
      const json = (await res.json()) as { settings: OnTimeRule };
      apply(json.settings);
      setLoadError(null);
    } catch {
      setLoadError('Network error — please try again.');
    }
  }, [opts]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loadError !== null) {
    return (
      <div style={{ color: 'var(--color-danger)' }}>
        <p style={{ marginBottom: '8px' }}>{loadError}</p>
        <Button variant="outline" onClick={(): void => void load()}>
          Retry
        </Button>
      </div>
    );
  }
  if (saved === null) {
    return (
      <p role="status" style={help}>
        Loading…
      </p>
    );
  }

  const parsed = parseRule(method, text);
  const unchanged =
    parsed.ok && parsed.rule.method === saved.method && parsed.rule.value === saved.value;

  const save = async (): Promise<void> => {
    if (!parsed.ok) return;
    setSaving(true);
    try {
      const res = await authedFetch(opts, '/v1/tenants/on-time-settings', {
        method: 'PATCH',
        body: JSON.stringify(parsed.rule),
      });
      if (!res.ok) {
        toast.error(`Save failed (${String(res.status)}).`);
        return;
      }
      const json = (await res.json()) as { settings: OnTimeRule };
      apply(json.settings);
      toast.success('On-time rule saved');
    } catch {
      toast.error('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const option = (m: OnTimeMethod, label: string): React.JSX.Element => (
    <label style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '32px' }}>
      <input
        type="radio"
        name="on-time-method"
        checked={method === m}
        onChange={(): void => {
          setMethod(m);
          setText(m === saved.method ? String(saved.value) : m === 'ALLOWANCE' ? '25' : '9');
        }}
      />
      {label}
    </label>
  );

  return (
    <div style={{ maxWidth: '560px' }}>
      <p style={{ ...help, marginBottom: '14px' }}>
        Each load is compared with earlier loads on the same route (same job type, pickup and
        drop-off) over the last 90 days, from all drivers, once there are at least 5 of them. Loads
        without enough history are left out rather than counted as late.
      </p>
      <fieldset style={{ border: 'none', padding: 0, marginBottom: '12px' }}>
        <legend style={{ fontWeight: 600, marginBottom: '6px' }}>Late means</legend>
        {option('ALLOWANCE', 'More than a set % slower than the route’s typical time')}
        {option('PERCENTILE', 'Slower than most earlier loads on the route')}
      </fieldset>
      <label
        htmlFor="on-time-value"
        style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}
      >
        {method === 'ALLOWANCE' ? 'Allowance (%)' : 'Out of 10 earlier loads'}
      </label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Input
          id="on-time-value"
          inputMode="numeric"
          value={text}
          aria-invalid={!parsed.ok}
          aria-describedby="on-time-help"
          onChange={(e): void => setText(e.target.value)}
          style={{ maxWidth: '120px' }}
        />
        <Button
          type="button"
          disabled={!parsed.ok || unchanged || saving}
          onClick={(): void => void save()}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
      <p id="on-time-help" style={{ ...help, marginTop: '6px' }}>
        {parsed.ok ? describeRule(parsed.rule) : parsed.message}
      </p>
    </div>
  );
}
